import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { cn } from "../lib/utils";
import {
  affichageDeLecture,
  ErreurColoriage,
  FilColoriage,
  LIEN_BOUTON,
  RelectureEchouee,
} from "./coloriage-commun";
import { ALERTE_GRISE, MessageDeRetour, type Retour } from "./coloriage-demandes";
import {
  messageDEchec,
  useAccueilColoriage,
  useActionSurVariante,
  useCahierColoriage,
  useComparaison,
  useModifierVariante,
  useNouvelleComparaison,
  useNouvelleVariante,
  useVariantes,
} from "./coloriage-requetes";
import {
  attenteDuTaux,
  auteurEnClair,
  comparaisonsEnClair,
  coutDeComparaison,
  etatVariante,
  NOM_REFERENCE,
  sujetsEnClair,
  type Taux,
  tauxEnClair,
  titrePage,
} from "./coloriage-textes";
import {
  type ListeVariantes,
  type NouvelleVariante,
  type PageResume,
  phraseEnregistreeUtilisable,
  phraseVarianteSchema,
  rogner,
  rognerCommePython,
  sujetVarianteSchema,
  type Variante,
} from "./contract";
import { compter } from "./format";
import { ConsoleShell } from "./shell";
import { Chargement } from "./states";

/**
 * Les variantes du prompt (`/coloriage/variantes`), ecran 4 du module : la table des
 * variantes et de leurs taux, le formulaire de celle qu'on ecrit, et le bloc qui lance une
 * comparaison. Textes et disposition : `ColoriageVariantes.dc.html`.
 *
 * Six regles tiennent l'ecran :
 *
 * - **La reference n'est pas une ligne en base.** Ses phrases vivent dans le paquet
 *   `coloring.prompts`, epinglees par un test : la console n'en garde aucune copie, qui
 *   deriverait. Elle est la premiere ligne de la table, avec ses compteurs (`liste.reference`),
 *   et une case toujours cochee et non decochable dans le bloc de lancement.
 * - **Un champ vide dit « comme la reference ».** Le formulaire ne montre donc jamais la
 *   phrase du paquet en aide, contrairement a la maquette : il dit « vide : la phrase de la
 *   reference ».
 * - **Une variante figee ne se modifie plus** (une version ou une comparaison l'emploie) :
 *   ses champs sont en lecture seule et « Dupliquer » en fait une copie reprenable.
 * - **Les taux ne comptent que les comparaisons closes** (contrat) : une variante deja
 *   employee mais dont la comparaison tourne encore n'affiche pas zero, elle dit qu'elle
 *   attend la cloture.
 * - **Un sujet remplace, ou son decor, a au moins une lettre ou un chiffre** (#285), la regle
 *   du contrat : un sujet `?` arretait toute la comparaison sur le PC, et un decor `...`
 *   faisait perdre le sien a la page. Le refus se dit sous le champ, « Enregistrer » s'eteint
 *   en disant pourquoi, et une variante ecrite avant la regle ne se compare plus sur la page
 *   de ce sujet (`varianteACorriger`, comme le backend).
 * - **Une phrase ecrite a, elle aussi, au moins une lettre ou un chiffre** (#293) : une phrase
 *   `?` s'enregistrait, puis le PC la remplacait par celle de la reference, et une comparaison
 *   mesurait la reference contre elle-meme. Meme refus sous le champ, meme « Enregistrer »
 *   eteint ; faite de blancs, une phrase dit encore « comme la reference », en silence. Une
 *   variante ecrite avant la regle ne se compare plus du tout : sa phrase vaut pour chaque page.
 */

/** Le nombre de versions par page d'une comparaison : de 1 a 10, 3 par defaut. */
const PAR_PAGE = Array.from({ length: 10 }, (_, rang) => rang + 1);
const PAR_PAGE_DEFAUT = 3;

/** Le contrat n'accepte que deux a cinq colonnes, la reference comprise. */
export const VARIANTES_MAX = 5;

/** Ce que le formulaire tient pendant qu'on ecrit : que des chaines, vides plutot que nulles. */
export type Brouillon = {
  readonly nom: string;
  readonly ouverture: string;
  readonly style: string;
  readonly rendu: string;
  readonly note: string;
  readonly sujets: readonly {
    readonly cle: string;
    readonly sujet: string;
    readonly decor: string;
  }[];
};

const BROUILLON_VIDE: Brouillon = {
  nom: "",
  ouverture: "",
  style: "",
  rendu: "",
  note: "",
  sujets: [],
};

/**
 * Le brouillon d'une variante lue : les nulls deviennent des chaines vides. Une phrase se montre
 * comme le PC la lira (`rognerCommePython`, #293) : un U+0085 enregistre avant la regle
 * s'affichait comme un carre, alors que le PC prenait la phrase de la reference.
 */
export function brouillonDe(variante: Variante | null): Brouillon {
  if (variante === null) return BROUILLON_VIDE;
  return {
    nom: variante.nom,
    ouverture: rognerCommePython(variante.ouverture ?? ""),
    style: rognerCommePython(variante.style ?? ""),
    rendu: rognerCommePython(variante.rendu ?? ""),
    note: variante.note ?? "",
    sujets: Object.entries(variante.sujets).map(([cle, remplace]) => ({
      cle,
      sujet: remplace.sujet,
      decor: remplace.decor ?? "",
    })),
  };
}

/** Une chaine de formulaire telle que le contrat la veut : vide normalisee en null. */
function ouNull(texte: string): string | null {
  const propre = texte.trim();
  return propre === "" ? null : propre;
}

/**
 * Une phrase de formulaire telle que le contrat la veut : rognee comme lui (`rogner`, le
 * `str.strip` de Python), vide normalisee en null. Un U+0085 seul, que `trim` garde, dit
 * « comme la reference », pour le contrat comme pour le PC (#293).
 */
function phraseOuNull(texte: string): string | null {
  const propre = rogner(texte);
  return propre === "" ? null : propre;
}

/**
 * Le corps que le backend attend. Un sujet sans texte est jete : la ligne a ete ajoutee puis
 * laissee vide, et un sujet vide serait refuse par le contrat. Sujet, decor et phrases se
 * rognent comme le contrat les rogne (`rogner`, le `str.strip` de Python) : un U+0085 seul, que
 * `trim` garde, est un vide, pas un texte que le contrat refuserait ou enregistrerait ensuite.
 */
export function corpsDeVariante(brouillon: Brouillon): NouvelleVariante {
  const sujets: Record<string, { sujet: string; decor: string | null }> = {};
  for (const ligne of brouillon.sujets) {
    const sujet = rogner(ligne.sujet);
    if (ligne.cle === "" || sujet === "") continue;
    const decor = rogner(ligne.decor);
    sujets[ligne.cle] = { sujet, decor: decor === "" ? null : decor };
  }
  return {
    nom: brouillon.nom.trim(),
    ouverture: phraseOuNull(brouillon.ouverture),
    style: phraseOuNull(brouillon.style),
    rendu: phraseOuNull(brouillon.rendu),
    sujets,
    note: ouNull(brouillon.note),
  };
}

/** Les trois phrases d'une variante, dans l'ordre du prompt (`CHAMPS_VARIANTE` du PC). */
const PHRASES = ["ouverture", "style", "rendu"] as const;
type Phrase = (typeof PHRASES)[number];

/** Une phrase dans un message, avec son article : les noms des champs du formulaire. */
const PHRASE_EN_CLAIR: Record<Phrase, string> = {
  ouverture: "une ouverture",
  style: "une phrase de style",
  rendu: "une phrase de rendu",
};

const PHRASE_SANS_LETTRE =
  "Aucune lettre de a à z ni chiffre : le modèle attend une phrase en anglais.";
const CORRIGER_PHRASE = "Écris-la, ou vide le champ pour garder celle de la référence.";
const PHRASE_TROP_LONGUE = "Plus de 2 000 caractères : raccourcis la phrase.";
const PHRASE_INVISIBLE =
  "Caractère invisible que le PC enverrait au modèle : enregistre la variante pour l'ôter.";

/** Ce que l'ecran dit d'une phrase, et si cela retient l'enregistrement. */
export type AvisDePhrase = { readonly message: string; readonly bloquant: boolean };

/**
 * Ce que l'ecran dit d'une phrase (#293), ou null. Ecrite sans lettre ni chiffre, ce n'est pas
 * une phrase en anglais : `?`, le PC la remplace par celle de la reference (`_ecrite`) ; `-`, il
 * l'envoie telle quelle. Faite de blancs, elle dit « comme la reference » : rien a dire.
 *
 * - Libre, elle se juge par le contrat (`phraseVarianteSchema`), sur ce que le champ
 *   enverrait : vide, null. Un refus retient l'enregistrement et dit sa cause, la longueur
 *   (une phrase plus longue n'arrive que par la base) ou les lettres. Acceptee par le contrat
 *   mais refusee telle qu'elle est en base (un U+FEFF entre des blancs que `trim` gardait :
 *   `rogner` l'ote, le PC l'envoie), elle ne retient rien, l'enregistrer l'ote ; la note le
 *   dit, puisque le lancement, lui, la refuse.
 * - Figee, elle ne se corrige pas : elle se juge telle qu'elle est en base, comme le PC la lira
 *   (`phraseEnregistreeUtilisable`, la regle de la garde du lancement), et le message ne dit
 *   que le pourquoi (l'encadre dit de la dupliquer).
 */
export function avisDeLaPhrase(texte: string, figee: boolean): AvisDePhrase | null {
  const phrase = texte === "" ? null : texte;
  if (figee) {
    return phraseEnregistreeUtilisable(phrase)
      ? null
      : { message: PHRASE_SANS_LETTRE, bloquant: true };
  }
  const lu = phraseVarianteSchema.safeParse(phrase);
  if (!lu.success) {
    const tropLongue = lu.error.issues.some((issue) => issue.code === "too_big");
    const message = tropLongue ? PHRASE_TROP_LONGUE : `${PHRASE_SANS_LETTRE} ${CORRIGER_PHRASE}`;
    return { message, bloquant: true };
  }
  if (phraseEnregistreeUtilisable(phrase)) return null;
  return { message: PHRASE_INVISIBLE, bloquant: false };
}

/** Ce que l'ecran dit d'une ligne des sujets remplaces (#285). */
export type AvisDeLigne = {
  /** Pourquoi le sujet ne passe pas le contrat, ou null. */
  readonly sujet: string | null;
  /** Pourquoi le decor ne passe pas le contrat, ou null. */
  readonly decor: string | null;
  /** Ce qui manque a une ligne commencee pour partir, ou null. Elle ne bloque rien. */
  readonly incomplete: string | null;
};

const SANS_LETTRE = "Aucune lettre de a à z ni chiffre : le modèle n'y trouverait rien à dessiner.";
const CORRIGER_SUJET = "Écris le sujet en anglais, avec au moins une lettre ou un chiffre.";
const CORRIGER_DECOR = "Écris le décor en anglais, ou vide le champ pour garder celui de la page.";

/**
 * Ce que l'ecran dit d'une ligne. Le verdict est celui du contrat (`sujetVarianteSchema`), sur
 * ce que la ligne enverrait (`corpsDeVariante`) : la regle ne vit qu'une fois. Un sujet `?`
 * faisait echouer toute la comparaison sur le PC (`prompt_klein`, « sujet vide ») ; un decor
 * `...` remplacait celui de la page, que klein otait ensuite (`_ecrite`).
 *
 * - Une ligne qui ne partira pas ne se juge pas. Vide, elle ne dit rien (on l'a ajoutee puis
 *   laissee) ; commencee, elle dit ce qui lui manque, sans rien bloquer.
 * - Une ligne qui part (une cle et un sujet) : un sujet ou un decor que le contrat refuse se dit
 *   sous son champ, et retient l'enregistrement.
 * - Figee, la variante ne se corrige pas : ses lignes enregistrees se jugent telles quelles, et
 *   le message ne dit que le pourquoi (l'encadre de la figee dit de la dupliquer).
 */
export function avisDeLaLigne(ligne: Brouillon["sujets"][number], figee: boolean): AvisDeLigne {
  if (!figee) {
    const manque = [
      ...(ligne.cle === "" ? ["la clé de page"] : []),
      ...(rogner(ligne.sujet) === "" ? ["le sujet"] : []),
    ];
    if (manque.length > 0) {
      const commencee = ligne.cle !== "" || rogner(ligne.sujet) + rogner(ligne.decor) !== "";
      const incomplete = commencee
        ? `Cette ligne ne partira pas : il lui manque ${manque.join(" et ")}.`
        : null;
      return { sujet: null, decor: null, incomplete };
    }
  }
  // Ce que la ligne enverrait, ou ce que la variante figee a enregistre : un decor vide est
  // null pour l'envoi, mais un U+0085 enregistre reste un texte, que le PC recevrait.
  const decor = (figee ? ligne.decor : rogner(ligne.decor)) === "" ? null : ligne.decor;
  const lu = sujetVarianteSchema.safeParse({ sujet: ligne.sujet, decor });
  const refuse = (champ: "sujet" | "decor"): boolean =>
    !lu.success && lu.error.issues.some((issue) => issue.path[0] === champ);
  const message = (corriger: string): string =>
    figee ? SANS_LETTRE : `${SANS_LETTRE} ${corriger}`;
  return {
    sujet: refuse("sujet") ? message(CORRIGER_SUJET) : null,
    decor: refuse("decor") ? message(CORRIGER_DECOR) : null,
    incomplete: null,
  };
}

/** Le nombre de lignes de sujets qui partiraient et que le contrat refuse (#285). */
function sujetsACorriger(brouillon: Brouillon): number {
  return brouillon.sujets.filter((ligne) => {
    const avis = avisDeLaLigne(ligne, false);
    return avis.sujet !== null || avis.decor !== null;
  }).length;
}

/** Pourquoi les sujets remplaces retiennent l'enregistrement, ou null : les lignes a corriger. */
export function refusDesSujets(brouillon: Brouillon): string | null {
  const aCorriger = sujetsACorriger(brouillon);
  if (aCorriger === 0) return null;
  return aCorriger === 1
    ? "Un sujet remplacé est à corriger, voir le message sous son champ."
    : `${aCorriger} sujets remplacés sont à corriger, voir les messages sous leurs champs.`;
}

/**
 * Pourquoi le formulaire retient l'enregistrement, ou null : les phrases (#293) puis les lignes
 * de sujets (#285) que le contrat refuse. Une note qui ne bloque rien ne compte pas. Sans
 * phrase a corriger, c'est le message des sujets, mot pour mot.
 */
export function refusDuBrouillon(brouillon: Brouillon): string | null {
  const phrases = PHRASES.filter(
    (champ) => avisDeLaPhrase(brouillon[champ], false)?.bloquant === true,
  );
  if (phrases.length === 0) return refusDesSujets(brouillon);
  const sujets = sujetsACorriger(brouillon);
  if (sujets === 0) {
    return phrases.length === 1
      ? "Une phrase est à corriger, voir le message sous son champ."
      : `${phrases.length} phrases sont à corriger, voir les messages sous leurs champs.`;
  }
  const lesPhrases = phrases.length === 1 ? "Une phrase" : `${phrases.length} phrases`;
  const lesSujets = sujets === 1 ? "un sujet remplacé" : `${sujets} sujets remplacés`;
  return `${lesPhrases} et ${lesSujets} sont à corriger, voir les messages sous leurs champs.`;
}

/**
 * Une variante cochee a corriger : une de ses phrases (#293), qui vaut pour chaque page, ou le
 * sujet qu'elle remplace sur une page cochee (#285).
 */
export type ACorriger =
  | { readonly variante: string; readonly phrase: Phrase }
  | { readonly variante: string; readonly page: string };

/**
 * La premiere variante cochee dont une phrase n'est pas en anglais, ou dont le sujet ou le
 * decor remplace sur une page cochee ne passe pas le contrat d'entree, ou null ; ses phrases
 * d'abord, comme le backend. Ecrite avant la regle, ou copiee d'une telle variante, elle se lit
 * encore ; le backend refuserait la comparaison (`variante_a_corriger`). Une phrase se juge
 * comme le PC la lira (`phraseEnregistreeUtilisable`), sur chaque page ; un sujet `?`, le PC
 * arreterait toute la demande sur lui.
 */
export function varianteACorriger(
  variantes: readonly Variante[],
  pages: readonly Pick<PageResume, "cle" | "rang" | "titre">[],
): ACorriger | null {
  for (const variante of variantes) {
    const phrase = PHRASES.find((champ) => !phraseEnregistreeUtilisable(variante[champ]));
    if (phrase !== undefined) return { variante: variante.nom, phrase };
    for (const page of pages) {
      const remplace = variante.sujets[page.cle];
      if (remplace !== undefined && !sujetVarianteSchema.safeParse(remplace).success) {
        return { variante: variante.nom, page: titrePage(page.rang, page.titre) };
      }
    }
  }
  return null;
}

/** Ce que le bloc de lancement enverra, et qui dit aussi si le bouton s'allume. */
export type Lancement = {
  readonly cahier: number | null;
  readonly pages: readonly number[];
  readonly versionsParPage: number;
  /** Les variantes cochees, la reference non comprise (elle part toujours, en `null`). */
  readonly variantes: readonly number[];
  /** Une variante cochee a corriger (`varianteACorriger`), ou rien. */
  readonly aCorriger?: ACorriger | null;
};

/** Pourquoi une comparaison ne peut pas partir, ou null quand elle peut. */
export function refusDeLancement(lancement: Lancement): string | null {
  if (lancement.cahier === null) return "Choisis un cahier.";
  if (lancement.pages.length === 0) return "Coche au moins une page.";
  if (lancement.variantes.length === 0) {
    return "Coche au moins une variante : la référence seule ne se compare à rien.";
  }
  if (lancement.variantes.length + 1 > VARIANTES_MAX) {
    return `${VARIANTES_MAX} variantes au plus, la référence comprise.`;
  }
  const aCorriger = lancement.aCorriger;
  if (aCorriger && "phrase" in aCorriger) {
    return `La variante « ${aCorriger.variante} » a ${PHRASE_EN_CLAIR[aCorriger.phrase]} sans lettre ni chiffre : le modèle attend une phrase en anglais. Corrige-la, ou duplique-la si elle est figée.`;
  }
  if (aCorriger) {
    return `La variante « ${aCorriger.variante} » a, pour la page ${aCorriger.page}, un sujet ou un décor sans lettre ni chiffre, que le PC ne sait pas rendre. Corrige-la, ou duplique-la si elle est figée.`;
  }
  return null;
}

export function ColoriageVariantesPage({ depuis }: { depuis: number | undefined }) {
  const lecture = useVariantes();
  // Jamais l'ecran d'erreur et la table ensemble (`affichageDeLecture`).
  const affichage = affichageDeLecture(lecture);

  return (
    <ConsoleShell connecte={affichage.contenu}>
      <FilColoriage>Variantes</FilColoriage>
      <header>
        <h1 className="text-2xl font-semibold">Variantes du prompt</h1>
        <p className="mt-1 max-w-[80ch] text-sm text-muted-foreground">
          Une variante change l'ouverture, la phrase de style, la phrase de rendu ou le sujet d'une
          page. Elle se compare à la référence sur les mêmes pages et les mêmes graines.
        </p>
      </header>
      {lecture.isPending ? <Chargement quoi="des variantes" /> : null}
      {affichage.ecranDErreur ? (
        <ErreurColoriage error={lecture.error} quoi="les variantes" />
      ) : null}
      {affichage.bandeau ? <RelectureEchouee error={lecture.error} /> : null}
      {affichage.contenu && lecture.data ? (
        <Variantes depuis={depuis} liste={lecture.data} />
      ) : null}
    </ConsoleShell>
  );
}

function Variantes({
  liste,
  depuis,
}: {
  liste: ListeVariantes;
  /** La comparaison dont « Relancer sur d'autres pages » vient : elle pre-remplit le bloc. */
  depuis: number | undefined;
}) {
  /**
   * La variante ouverte dans le panneau de droite, **telle quelle** et non par son identifiant :
   * `{ variante: null }` est une nouvelle, `null` est un panneau ferme.
   *
   * L'ecran tenait un identifiant et allait chercher la variante dans la liste lue. Une copie
   * tout juste creee (« Dupliquer ») n'y est pas encore : le panneau s'ouvrait alors vide, la
   * `key` ne bougeait plus quand la liste arrivait, et l'enregistrer renvoyait des phrases
   * nulles, donc effacait la copie. La liste ne sert plus qu'a choisir ; ce que le serveur vient
   * de rendre fait foi.
   */
  const [ouverte, setOuverte] = useState<{ readonly variante: Variante | null } | null>(null);
  const [retour, setRetour] = useState<Retour | null>(null);
  const vivantes = liste.variantes.filter((une) => !une.archivee);
  const archivees = liste.variantes.filter((une) => une.archivee);

  const poser = (variante: Variante | null): void => {
    setRetour(null);
    setOuverte({ variante });
  };

  return (
    <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_34rem]">
      <div className="flex min-w-0 flex-col gap-5">
        <TableDesVariantes
          choisie={ouverte?.variante?.id ?? null}
          liste={liste}
          montrer={poser}
          nouvelle={() => poser(null)}
          vivantes={vivantes}
        />
        {archivees.length === 0 ? null : <Archivees variantes={archivees} />}
        <LancerUneComparaison depuis={depuis} variantes={vivantes} />
      </div>
      {ouverte === null ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Aucune variante ouverte</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Choisis une variante dans la table pour la lire, ou écris-en une nouvelle.
          </CardContent>
        </Card>
      ) : (
        <FormulaireVariante
          annoncer={setRetour}
          fermer={() => setOuverte(null)}
          // Changer de variante remonte le formulaire, donc repart de son brouillon. Une
          // modification enregistree garde la meme cle : ce que Nicolas est en train d'ecrire ne
          // se fait pas remplacer par l'echo du serveur.
          key={ouverte.variante?.id ?? "nouvelle"}
          ouvrir={(variante) => setOuverte({ variante })}
          retour={retour}
          variante={ouverte.variante}
        />
      )}
    </div>
  );
}

const CELLULE = "border-t border-border px-3 py-2.5 align-top";
const ENTETE = "px-3 pb-2 font-semibold";

function TableDesVariantes({
  liste,
  vivantes,
  choisie,
  montrer,
  nouvelle,
}: {
  liste: ListeVariantes;
  vivantes: readonly Variante[];
  choisie: number | null;
  montrer: (variante: Variante) => void;
  nouvelle: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base" id="coloriage-variantes">
          Toutes les variantes
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3.5">
        {/* Quatre colonnes de nombres : sur telephone la table defile a l'horizontale. */}
        <div className="overflow-x-auto">
          <table
            aria-labelledby="coloriage-variantes"
            className="w-full min-w-[34rem] border-collapse text-sm"
          >
            <thead>
              <tr className="text-left text-xs font-semibold text-muted-foreground">
                <th className={ENTETE} scope="col">
                  Variante
                </th>
                <th className={ENTETE} scope="col">
                  Auteur
                </th>
                <th className={ENTETE} scope="col">
                  Comparaisons
                </th>
                <th className={cn(ENTETE, "w-[13rem]")} scope="col">
                  Versions bonnes
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={CELLULE}>
                  <strong className="font-semibold">{NOM_REFERENCE}</strong>
                  <span className="block text-xs text-muted-foreground">
                    le prompt du paquet, épinglé par un test
                  </span>
                </td>
                <td className={CELLULE}>{auteurEnClair(null)}</td>
                <td className={CELLULE}>{comparaisonsEnClair(liste.reference.comparaisons)}</td>
                <td className={CELLULE}>
                  {/* La reference n'est jamais figee : ses phrases vivent dans le paquet. */}
                  <ColonneTaux figee={false} taux={liste.reference} />
                </td>
              </tr>
              {vivantes.map((variante) => (
                <LigneVariante
                  choisie={variante.id === choisie}
                  key={variante.id}
                  montrer={() => montrer(variante)}
                  variante={variante}
                />
              ))}
            </tbody>
          </table>
        </div>
        {vivantes.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Aucune variante pour l'instant : la référence est seule. Écris-en une, ou lance
            <code className="mx-1 rounded bg-muted px-1.5 py-0.5 text-foreground">
              coloring console variante ajouter
            </code>
            depuis le PC.
          </p>
        ) : null}
        <div>
          <Button onClick={nouvelle} variant="outline">
            Nouvelle variante
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function LigneVariante({
  variante,
  choisie,
  montrer,
}: {
  variante: Variante;
  choisie: boolean;
  montrer: () => void;
}) {
  const sujets = sujetsEnClair(variante.sujets);
  return (
    <tr className={choisie ? "bg-muted/60" : undefined}>
      <td className={CELLULE}>
        <button
          aria-current={choisie ? "true" : undefined}
          className="text-left font-semibold underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={montrer}
          type="button"
        >
          {variante.nom}
        </button>
        <span className="block text-xs text-muted-foreground">
          {[etatVariante(variante), sujets].filter((morceau) => morceau !== "").join(" · ")}
        </span>
      </td>
      <td className={CELLULE}>{auteurEnClair(variante.auteur)}</td>
      <td className={CELLULE}>{comparaisonsEnClair(variante.comparaisons)}</td>
      <td className={CELLULE}>
        <ColonneTaux figee={variante.figee} taux={variante} />
      </td>
    </tr>
  );
}

/** Le taux d'une variante, ou ce qu'elle attend pour en avoir un. */
function ColonneTaux({ taux, figee }: { taux: Taux; figee: boolean }) {
  const attente = attenteDuTaux({ ...taux, figee });
  if (attente !== null) return <span className="text-xs text-muted-foreground">{attente}</span>;
  return <span className="tabular-nums">{tauxEnClair(taux)}</span>;
}

/** Les variantes archivees, repliees : elles ne s'effacent pas, elles sortent de la liste. */
function Archivees({ variantes }: { variantes: readonly Variante[] }) {
  return (
    <details className="rounded-lg border border-border bg-card px-5 py-3.5 text-card-foreground">
      <summary className="cursor-pointer text-sm font-semibold">
        {compter(variantes.length, "variante archivée", "variantes archivées")}
      </summary>
      <ul className="mt-2.5 flex flex-col gap-1.5 text-sm text-muted-foreground">
        {variantes.map((variante) => (
          <li key={variante.id}>
            {variante.nom} · {auteurEnClair(variante.auteur)} ·{" "}
            {comparaisonsEnClair(variante.comparaisons)}
          </li>
        ))}
      </ul>
      <p className="mt-2.5 text-xs text-muted-foreground">
        Une variante archivée garde ses versions et ses jugements : elle ne se compare plus.
      </p>
    </details>
  );
}

const ETIQUETTE = "text-xs font-semibold uppercase tracking-wide text-muted-foreground";
const CHAMP =
  "w-full rounded-md border border-input bg-background px-2.5 py-2 text-sm leading-normal placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60";

/**
 * Le formulaire d'une variante. `variante` a null : une nouvelle. Une variante figee est en
 * lecture seule : le backend refuserait sa modification (409 `variante_figee`), autant ne pas
 * offrir un bouton qui ne mene qu'a un refus.
 */
function FormulaireVariante({
  variante,
  retour,
  annoncer,
  fermer,
  ouvrir,
}: {
  variante: Variante | null;
  retour: Retour | null;
  annoncer: (retour: Retour | null) => void;
  fermer: () => void;
  /** Ouvre la variante que le serveur vient de rendre, telle quelle : voir `Variantes`. */
  ouvrir: (variante: Variante) => void;
}) {
  const [brouillon, setBrouillon] = useState<Brouillon>(() => brouillonDe(variante));
  const creer = useNouvelleVariante();
  const modifier = useModifierVariante();
  const action = useActionSurVariante();
  // Un verrou synchrone : un double clic arrive avant que `isPending` ne soit rendu.
  const envoi = useRef(false);
  const figee = variante?.figee === true;
  const occupe = creer.isPending || modifier.isPending || action.isPending;
  // Une phrase (#293), un sujet ou un decor (#285) que le contrat refuserait : dit sous son
  // champ, et ici.
  const refusDuFormulaire = refusDuBrouillon(brouillon);
  const idRefus = useId();

  const poser = (morceau: Partial<Brouillon>): void =>
    setBrouillon((avant) => ({ ...avant, ...morceau }));

  const enregistrer = (): void => {
    if (envoi.current || brouillon.nom.trim() === "" || refusDuFormulaire !== null) return;
    envoi.current = true;
    annoncer(null);
    const corps = corpsDeVariante(brouillon);
    const fini = {
      onSuccess: (reponse: { variante: Variante }) => {
        annoncer({ ton: "ok", texte: `Variante « ${reponse.variante.nom} » enregistrée.` });
        ouvrir(reponse.variante);
      },
      onError: (error: unknown) => annoncer({ ton: "echec", texte: messageDEchec(error) }),
      onSettled: () => {
        envoi.current = false;
      },
    };
    if (variante === null) creer.mutate(corps, fini);
    else modifier.mutate({ id: variante.id, ...corps }, fini);
  };

  const agir = (quoi: "dupliquer" | "archiver"): void => {
    if (variante === null || envoi.current) return;
    envoi.current = true;
    annoncer(null);
    action.mutate(
      { id: variante.id, action: quoi },
      {
        onSuccess: (reponse) => {
          if (quoi === "dupliquer") {
            annoncer({ ton: "ok", texte: `Copie ouverte : « ${reponse.variante.nom} ».` });
            ouvrir(reponse.variante);
          } else {
            annoncer({ ton: "ok", texte: `Variante « ${variante.nom} » archivée.` });
            fermer();
          }
        },
        onError: (error) => annoncer({ ton: "echec", texte: messageDEchec(error) }),
        onSettled: () => {
          envoi.current = false;
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {variante === null ? "Nouvelle variante" : variante.nom}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3.5">
        {figee ? (
          <p className={ALERTE_GRISE}>
            <strong className="font-semibold">Figée.</strong> Une version ou une comparaison
            l'emploie : elle ne se modifie plus, pour que ses versions gardent le prompt qui les a
            faites. « Dupliquer » en fait une copie reprenable.
          </p>
        ) : null}
        <Ligne etiquette="Nom">
          <input
            aria-label="Nom"
            className={CHAMP}
            disabled={figee}
            maxLength={80}
            onChange={(event) => poser({ nom: event.target.value })}
            placeholder="Trait épais, sans détails"
            value={brouillon.nom}
          />
        </Ligne>
        <ChampPhrase
          aide="Ce qui précède le sujet. Vide : la phrase de la référence."
          changer={(ouverture) => poser({ ouverture })}
          etiquette="Ouverture"
          figee={figee}
          lignes={2}
          texte={brouillon.ouverture}
        />
        <ChampPhrase
          aide="Vide : la phrase de la référence."
          changer={(style) => poser({ style })}
          etiquette="Phrase de style"
          figee={figee}
          lignes={3}
          texte={brouillon.style}
        />
        <ChampPhrase
          aide="Vide : la phrase de la référence."
          changer={(rendu) => poser({ rendu })}
          etiquette="Phrase de rendu"
          figee={figee}
          lignes={3}
          texte={brouillon.rendu}
        />
        <SujetsRemplaces brouillon={brouillon} figee={figee} poser={poser} />
        <Ligne etiquette="Note">
          <textarea
            aria-label="Note"
            className={CHAMP}
            disabled={figee}
            maxLength={500}
            onChange={(event) => poser({ note: event.target.value })}
            placeholder="Pourquoi cette variante, si besoin"
            rows={2}
            value={brouillon.note}
          />
        </Ligne>
        <div className="flex flex-wrap items-center gap-2">
          {figee ? null : (
            <Button
              aria-describedby={refusDuFormulaire === null ? undefined : idRefus}
              aria-disabled={occupe || undefined}
              className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              disabled={brouillon.nom.trim() === "" || refusDuFormulaire !== null}
              onClick={enregistrer}
            >
              Enregistrer
            </Button>
          )}
          {variante === null ? null : (
            <>
              <button
                className={cn(LIEN_BOUTON, "disabled:pointer-events-none disabled:opacity-50")}
                onClick={() => agir("dupliquer")}
                type="button"
              >
                Dupliquer
              </button>
              <button
                className={cn(LIEN_BOUTON, "disabled:pointer-events-none disabled:opacity-50")}
                onClick={() => agir("archiver")}
                type="button"
              >
                Archiver
              </button>
            </>
          )}
        </div>
        {figee ? null : (
          // Toujours dans la page, vide ou non : une region vivante inseree avec son texte n'est
          // pas toujours lue. Vide, elle ne prend aucune place (`sr-only`).
          <p
            aria-live="polite"
            className={refusDuFormulaire === null ? "sr-only" : "text-sm text-muted-foreground"}
            id={idRefus}
          >
            {refusDuFormulaire ?? ""}
          </p>
        )}
        <MessageDeRetour retour={retour} />
      </CardContent>
    </Card>
  );
}

function Ligne({
  etiquette,
  aide,
  children,
}: {
  etiquette: string;
  aide?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className={ETIQUETTE}>{etiquette}</span>
      {children}
      {aide === undefined ? null : <span className="text-xs text-muted-foreground">{aide}</span>}
    </div>
  );
}

/**
 * Une phrase de la variante (ouverture, style ou rendu), et ce que l'ecran en dit
 * (`avisDeLaPhrase`, #293) sous le champ, relie a lui : une phrase ecrite sans lettre ni
 * chiffre, meme sur une variante figee (enregistree avant la regle, elle dit ainsi pourquoi
 * elle ne se compare plus), ou la note d'un caractere invisible que l'enregistrement otera,
 * sans couleur d'erreur puisqu'elle ne retient rien. Vide, elle dit « comme la reference » :
 * l'aide le rappelle.
 */
function ChampPhrase({
  etiquette,
  aide,
  lignes,
  texte,
  figee,
  changer,
}: {
  etiquette: string;
  aide: string;
  lignes: number;
  texte: string;
  figee: boolean;
  changer: (texte: string) => void;
}) {
  const idAvis = useId();
  const avis = avisDeLaPhrase(texte, figee);
  return (
    <Ligne aide={aide} etiquette={etiquette}>
      <textarea
        aria-describedby={avis === null ? undefined : idAvis}
        aria-invalid={avis?.bloquant || undefined}
        aria-label={etiquette}
        className={CHAMP}
        disabled={figee}
        maxLength={2000}
        onChange={(event) => changer(event.target.value)}
        rows={lignes}
        value={texte}
      />
      {avis === null ? null : (
        <p
          className={cn("text-xs", avis.bloquant ? "text-destructive" : "text-muted-foreground")}
          id={idAvis}
        >
          {avis.message}
        </p>
      )}
    </Ligne>
  );
}

/**
 * Les sujets qu'une variante remplace, page par page. La cle est celle de la page dans le
 * recueil (`11-l-epouvantail`) : le PC s'en sert pour retrouver quel sujet remplacer. Elle
 * s'ecrit a la main, parce que la liste des pages appartient a un cahier et qu'une variante
 * n'appartient a aucun.
 *
 * Ce que dit chaque ligne vient d'`avisDeLaLigne` (#285) : un sujet ou un decor sans lettre ni
 * chiffre sous son champ, meme sur une variante figee (enregistree avant la regle, elle dit
 * ainsi pourquoi elle ne se compare plus sur cette page), et ce qui manque a une ligne
 * commencee qui ne partira pas.
 */
function SujetsRemplaces({
  brouillon,
  figee,
  poser,
}: {
  brouillon: Brouillon;
  figee: boolean;
  poser: (morceau: Partial<Brouillon>) => void;
}) {
  const prefixe = useId();
  const remplacer = (rang: number, morceau: Partial<Brouillon["sujets"][number]>): void =>
    poser({
      sujets: brouillon.sujets.map((ligne, autre) =>
        autre === rang ? { ...ligne, ...morceau } : ligne,
      ),
    });

  return (
    <fieldset className="flex flex-col gap-2.5">
      <legend className={ETIQUETTE}>Sujets remplacés</legend>
      {brouillon.sujets.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Aucun : la variante garde les sujets du recueil.
        </p>
      ) : null}
      {brouillon.sujets.map((ligne, rang) => {
        const avis = avisDeLaLigne(ligne, figee);
        const idSujet = `${prefixe}-${rang}-sujet`;
        const idDecor = `${prefixe}-${rang}-decor`;
        const idManque = `${prefixe}-${rang}-manque`;
        return (
          <div
            className="flex flex-col gap-1.5 rounded-md border border-border bg-background p-2.5"
            // Les lignes s'ajoutent et se retirent par leur rang : deux lignes peuvent porter la
            // meme cle le temps qu'on la corrige, donc la cle ne fait pas un identifiant.
            // biome-ignore lint/suspicious/noArrayIndexKey: rang stable tant que la ligne existe
            key={rang}
          >
            <input
              aria-describedby={avis.incomplete === null ? undefined : idManque}
              aria-label={`Clé de page ${rang + 1}`}
              className={CHAMP}
              disabled={figee}
              maxLength={160}
              onChange={(event) => remplacer(rang, { cle: event.target.value })}
              placeholder="11-l-epouvantail"
              value={ligne.cle}
            />
            <textarea
              aria-describedby={
                avis.sujet !== null ? idSujet : avis.incomplete !== null ? idManque : undefined
              }
              aria-invalid={avis.sujet !== null || undefined}
              aria-label={`Sujet ${rang + 1}`}
              className={CHAMP}
              disabled={figee}
              maxLength={2000}
              onChange={(event) => remplacer(rang, { sujet: event.target.value })}
              placeholder="a scarecrow made of straw on a wooden cross-shaped pole"
              rows={2}
              value={ligne.sujet}
            />
            {avis.sujet === null ? null : (
              <p className="text-xs text-destructive" id={idSujet}>
                {avis.sujet}
              </p>
            )}
            <input
              aria-describedby={avis.decor === null ? undefined : idDecor}
              aria-invalid={avis.decor !== null || undefined}
              aria-label={`Décor ${rang + 1}`}
              className={CHAMP}
              disabled={figee}
              maxLength={2000}
              onChange={(event) => remplacer(rang, { decor: event.target.value })}
              placeholder="Décor, si la variante le remplace aussi"
              value={ligne.decor}
            />
            {avis.decor === null ? null : (
              <p className="text-xs text-destructive" id={idDecor}>
                {avis.decor}
              </p>
            )}
            {avis.incomplete === null ? null : (
              <p className="text-xs text-muted-foreground" id={idManque}>
                {avis.incomplete}
              </p>
            )}
            {figee ? null : (
              <div>
                <button
                  className={cn(LIEN_BOUTON, "min-h-9 px-3 py-1.5 text-xs")}
                  onClick={() =>
                    poser({ sujets: brouillon.sujets.filter((_, autre) => autre !== rang) })
                  }
                  type="button"
                >
                  Retirer ce sujet
                </button>
              </div>
            )}
          </div>
        );
      })}
      {figee ? null : (
        <div>
          <button
            className={cn(LIEN_BOUTON, "min-h-9 px-3 py-1.5 text-xs")}
            onClick={() =>
              poser({ sujets: [...brouillon.sujets, { cle: "", sujet: "", decor: "" }] })
            }
            type="button"
          >
            Remplacer un autre sujet
          </button>
        </div>
      )}
    </fieldset>
  );
}

/**
 * Le bloc qui lance une comparaison : un cahier, ses pages cochees, le nombre de versions par
 * page, et les variantes en colonnes. Seuls les cahiers ouverts et rendus par un modele que le
 * PC sait relancer sont proposes : un cahier clos ou rendu par un workflow ComfyUI serait
 * refuse par le backend (409 `cahier_clos`, 400 `rendu_impossible`).
 */
function LancerUneComparaison({
  variantes,
  depuis,
}: {
  variantes: readonly Variante[];
  depuis: number | undefined;
}) {
  const navigate = useNavigate();
  const accueil = useAccueilColoriage();
  const precedente = useComparaison(String(depuis ?? ""), depuis !== undefined);
  const lancer = useNouvelleComparaison();
  const [cahier, setCahier] = useState<number | null>(null);
  const [cochees, setCochees] = useState<ReadonlySet<number>>(new Set());
  const [parPage, setParPage] = useState(PAR_PAGE_DEFAUT);
  const [colonnes, setColonnes] = useState<ReadonlySet<number>>(new Set());
  const [retour, setRetour] = useState<Retour | null>(null);
  const envoi = useRef(false);
  const preRempli = useRef(false);
  const idRefus = useId();

  const comparables = (accueil.data?.cahiers ?? []).filter(
    (une) => une.etat === "ouvert" && une.modele !== "workflow",
  );
  const choisi = cahier ?? comparables[0]?.id ?? null;
  const detail = useCahierColoriage(String(choisi ?? ""), choisi !== null);
  const pages = detail.data?.pages ?? [];

  // « Relancer sur d'autres pages » : le cahier, le nombre de versions et les variantes de la
  // comparaison precedente, mais aucune de ses pages. Une seule fois, pour que la lecture qui
  // se rejoue n'efface pas les cases que Nicolas vient de cocher.
  useEffect(() => {
    const lue = precedente.data;
    if (lue === undefined || lue.tableau === null || preRempli.current) return;
    preRempli.current = true;
    setCahier(lue.cahier.id);
    setParPage(lue.versions_par_page);
    setColonnes(
      new Set(
        lue.tableau
          .map((ligne) => ligne.variante?.id)
          .filter((id): id is number => id !== undefined),
      ),
    );
    setCochees(new Set());
  }, [precedente.data]);

  const pagesCochees = pages.filter((page) => cochees.has(page.id));
  const variantesCochees = variantes.filter((une) => colonnes.has(une.id));
  const lancement: Lancement = {
    cahier: choisi,
    pages: pagesCochees.map((page) => page.id),
    versionsParPage: parPage,
    variantes: variantesCochees.map((une) => une.id),
    aCorriger: varianteACorriger(variantesCochees, pagesCochees),
  };
  const refus = refusDeLancement(lancement);

  const envoyer = (): void => {
    if (envoi.current || refus !== null || lancement.cahier === null) return;
    envoi.current = true;
    setRetour(null);
    lancer.mutate(
      {
        cahier: lancement.cahier,
        pages: [...lancement.pages],
        versions_par_page: lancement.versionsParPage,
        // La reference part toujours, ecrite `null` : c'est une variante comme les autres.
        variantes: [null, ...lancement.variantes],
      },
      {
        onSuccess: (reponse) => {
          void navigate({
            to: "/coloriage/comparaisons/$id",
            params: { id: String(reponse.comparaison.id) },
          });
        },
        onError: (error) => setRetour({ ton: "echec", texte: messageDEchec(error) }),
        onSettled: () => {
          envoi.current = false;
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Lancer une comparaison</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3.5">
        {comparables.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Aucun cahier à comparer : il en faut un ouvert, rendu par un modèle que le PC sait
            relancer.
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-1.5">
              <span className={ETIQUETTE}>Cahier</span>
              <select
                aria-label="Cahier"
                className="h-10 w-fit max-w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onChange={(event) => {
                  setCahier(Number(event.target.value));
                  setCochees(new Set());
                }}
                value={choisi ?? ""}
              >
                {comparables.map((une) => (
                  <option key={une.id} value={une.id}>
                    {une.titre}
                  </option>
                ))}
              </select>
            </div>
            <PagesACocher
              basculer={(id) => {
                const suite = new Set(cochees);
                if (suite.has(id)) suite.delete(id);
                else suite.add(id);
                setCochees(suite);
              }}
              cochees={cochees}
              enCours={detail.isPending}
              pages={pages}
            />
            <div className="flex flex-wrap items-center gap-2">
              <span className={ETIQUETTE}>Versions par page</span>
              <select
                aria-label="Versions par page"
                className="h-10 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onChange={(event) => setParPage(Number(event.target.value))}
                value={parPage}
              >
                {PAR_PAGE.map((nombre) => (
                  <option key={nombre} value={nombre}>
                    {nombre}
                  </option>
                ))}
              </select>
            </div>
            <ColonnesACocher
              basculer={(id) => {
                const suite = new Set(colonnes);
                if (suite.has(id)) suite.delete(id);
                else suite.add(id);
                setColonnes(suite);
              }}
              cochees={colonnes}
              variantes={variantes}
            />
            <p className={ALERTE_GRISE}>
              {coutDeComparaison(
                lancement.pages.length,
                lancement.versionsParPage,
                lancement.variantes.length + 1,
              )}{" "}
              Chaque variante reçoit les mêmes graines : seule la phrase change.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                aria-describedby={refus === null ? undefined : idRefus}
                aria-disabled={lancer.isPending || undefined}
                className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
                disabled={refus !== null}
                onClick={envoyer}
              >
                Lancer la comparaison
              </Button>
              {/* Toujours dans la page, vide ou non, comme la raison d'« Enregistrer » : une
                  region vivante inseree avec son texte n'est pas toujours lue. */}
              <span
                aria-live="polite"
                className={refus === null ? "sr-only" : "text-sm text-muted-foreground"}
                id={idRefus}
              >
                {refus ?? ""}
              </span>
            </div>
            <MessageDeRetour retour={retour} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PagesACocher({
  pages,
  cochees,
  enCours,
  basculer,
}: {
  pages: readonly PageResume[];
  cochees: ReadonlySet<number>;
  enCours: boolean;
  basculer: (id: number) => void;
}) {
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className={ETIQUETTE}>Pages</legend>
      {enCours ? <Chargement quoi="des pages" /> : null}
      {!enCours && pages.length === 0 ? (
        <p className="text-xs text-muted-foreground">Ce cahier n'a encore aucune page.</p>
      ) : null}
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {pages.map((page) => (
          <label className="flex items-center gap-2 text-sm" key={page.id}>
            <input
              checked={cochees.has(page.id)}
              className="size-4 accent-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              onChange={() => basculer(page.id)}
              type="checkbox"
            />
            {titrePage(page.rang, page.titre)}
          </label>
        ))}
      </div>
      {pages.length === 0 ? null : (
        <span className="text-xs text-muted-foreground">
          {cochees.size} sur {pages.length}
        </span>
      )}
    </fieldset>
  );
}

function ColonnesACocher({
  variantes,
  cochees,
  basculer,
}: {
  variantes: readonly Variante[];
  cochees: ReadonlySet<number>;
  basculer: (id: number) => void;
}) {
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className={ETIQUETTE}>Variantes</legend>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {/* La reference part toujours : sa case est cochee et eteinte, jamais decochable. */}
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            aria-label="Référence, toujours"
            checked
            className="size-4 accent-foreground"
            disabled
            readOnly
            type="checkbox"
          />
          Référence, toujours
        </label>
        {variantes.map((variante) => (
          <label className="flex items-center gap-2 text-sm" key={variante.id}>
            <input
              checked={cochees.has(variante.id)}
              className="size-4 accent-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              onChange={() => basculer(variante.id)}
              type="checkbox"
            />
            {variante.nom}
          </label>
        ))}
      </div>
      {variantes.length === 0 ? (
        <span className="text-xs text-muted-foreground">
          Écris une variante pour avoir quelque chose à comparer.
        </span>
      ) : null}
    </fieldset>
  );
}
